import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import ConfirmModal from "../../../../utils/modal/confirm";
import IntlMessages from "../../../../utils/messages";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import { QuoteRequestService } from "../../../../lib/ground-aws-graphql-core/models/Api/QuoteRequests";

interface Props {
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListQuoteRequestServices = (props: Props): JSX.Element => {
  const { intl, location } = props;
  const [loading, setLoading] = useState(false);

  const [
    showDeleteQuoteRequestServiceModal,
    setShowDeleteQuoteRequestServiceModal,
  ] = useState(false);

  const [quoteRequestServiceId, setQuoteRequestServiceId] = useState<number>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listQuoteRequestServices = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.listQuoteRequestServices
  );

  const quoteRequestServices = GroundGraphqlContextStore.useStoreState(
    actions => actions.quoteRequests.quoteRequestServices.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.quoteRequests.quoteRequestServices.total
  );

  const setQuoteRequestService = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.setQuoteRequestService
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteQuoteRequestService = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.deleteQuoteRequestService
  );

  useEffect(() => {
    if (location?.state) {
      fetchData(location?.state?.pageIndex, true, location?.state?.limit);
    } else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    listQuoteRequestServices({
      offset: pageIndex * limit,
      limit,
    }).finally(() => setLoading(false));
  };

  const addNewQuoteRequestService = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_SERVICES}/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditQuoteRequestService = (id: number) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_SERVICES}/${id}/${EnumPaths.EDIT_MODE}`;
    setQuoteRequestService(null);
    history.push(editLink);
  };

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteQuoteRequestServiceModal(false);
    deleteQuoteRequestService({ id: quoteRequestServiceId! })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.request.service.delete.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.request.service.delete.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableHead = [
    "page.quote.request.service.label",
    "page.quote.request.service.code",
    "page.quote.request.service.created_at",
    "page.quote.request.service.updated_at",
    "general.actions",
  ];

  const tableBody = quoteRequestServices.map(
    (quoteRequestService: QuoteRequestService) => {
      const { id, code, label, created_at, updated_at } = quoteRequestService;
      const editLink = `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_SERVICES}/${id}/${EnumPaths.EDIT_MODE}`;

      const dropdownValues: DropdownValues[] = [
        {
          id: "edit_quote_request_service_key",
          label: "page.list.quote.request.service.edit",
          icon: images.edit,
          link: editLink,
        },
        {
          id: "delete",
          label: "general.delete",
          icon: images.deleteIcon,
          onClick: () => {
            setShowDeleteQuoteRequestServiceModal(
              !showDeleteQuoteRequestServiceModal
            );
            setQuoteRequestServiceId(id);
          },
        },
      ];

      const elements = [
        {
          element: getTranslation(label),
          onCellClick: () => handleEditQuoteRequestService(id),
        },
        {
          element: code,
          onCellClick: () => handleEditQuoteRequestService(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
          onCellClick: () => handleEditQuoteRequestService(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
          onCellClick: () => handleEditQuoteRequestService(id),
        },
        {
          element: (
            <Dropdown
              values={dropdownValues}
              dataCy={`quote-request-service-dropdown-${id}`}
            />
          ),
        },
      ];

      return {
        id: quoteRequestService.id.toString(),
        rowElements: elements,
      };
    }
  );

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.quote.request.services.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.quote.request.services.title"
        nbOfResults={total}
        buttonRightTitle="page.list.quote.request.services.create.quote.request.service"
        buttonRightAction={addNewQuoteRequestService}
        buttonRightId="btn-create-quote-request-service"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.quote.request.services.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteQuoteRequestServiceModal}
        onRequestClose={() =>
          setShowDeleteQuoteRequestServiceModal(
            !showDeleteQuoteRequestServiceModal
          )
        }
        toggle={() =>
          setShowDeleteQuoteRequestServiceModal(
            !showDeleteQuoteRequestServiceModal
          )
        }
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.quote.request.services.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListQuoteRequestServices);
